import 'features/lots/components/dangerButton.scss';

import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import PPartDisplay from 'components/Projects/pPartDisplay';
import fetchPartLabelsForLots from 'features/lots/api/fetchPartLabelsForLots';
import LotKindBadge from 'features/lots/components/LotKindBadge';
import LotStatusBadge from 'features/lots/components/LotStatusBadge';
import QuarantineLotModal from 'features/lots/components/QuarantineLotModal';
import useLotTrace from 'features/lots/hooks/useLotTrace';
import buildLotTraceCsv from 'features/lots/utils/buildLotTraceCsv';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import TraceLotNode from './components/TraceLotNode';

/**
 * Downstream trace: scan a bag (or arrive via a lot detail page's Trace
 * button, ?lotId=...) and see everything built from it — assembled lots
 * hop by hop and the travelers they were installed on, with shipped vs
 * in-stock visible per row and summed in the tiles. The root lot lives
 * in the URL so a trace is shareable.
 */
export default function TraceLotPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const lotId = searchParams.get('lotId');
  const { data: trace, isLoading, error } = useLotTrace(lotId);
  const [showQuarantine, setShowQuarantine] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const partLabels = await fetchPartLabelsForLots(Object.values(trace.lotsById));
      const csv = buildLotTraceCsv(trace, partLabels);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `trace-${trace.root.code || trace.root.id}-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      // Deferred — an immediate revoke can cancel the download in some
      // browsers before they start reading the blob.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (exportError) {
      toast.error(`Could not export: ${exportError.message || exportError}`);
    } finally {
      setIsExporting(false);
    }
  };

  const travelers = Object.values(trace?.travelersById || {});
  const downstreamLotCount = trace ? Object.keys(trace.lotsById).length - 1 : 0;
  const shippedCount = travelers.filter((traveler) => traveler.shipped).length;
  // Where the traveler actually went: inventory travelers share one
  // internal order but can ship against different customer orders, so
  // prefer the shipped line item over the originating order. Line items
  // are order lines — one order with two shipped lines counts twice, so
  // the tile says "Order lines", not "Orders".
  const orderLineCount = new Set(
    travelers.map((traveler) => traveler.shippedLineItemId || traveler.order).filter(Boolean),
  ).size;

  return (
    <SpaceBetween size="l">
      <Container
        header={(
          <Header
            variant="awsui-h1-sticky"
            description="Scan a bag to see everything built from it — assembled lots and the travelers they were installed on."
          >
            Trace lot
          </Header>
        )}
      >
        <ScanField
          id="trace-scanner"
          accept={[ScanTypes.LOT]}
          onScan={({ id }) => setSearchParams({ lotId: id })}
          autoFocus
          placeholder="Scan or type a bag…"
        />
      </Container>

      {lotId && isLoading && <Spinner size="large" />}
      {error && <Alert type="error">{error.message}</Alert>}

      {trace && (
        <>
          <Container
            header={(
              <Header
                variant="h2"
                description={(
                  <PPartDisplay
                    fieldName="fullId"
                    projectDocId={trace.root.projectDocId}
                    pPartDocId={trace.root.pPartDocId}
                    fallbackMessage="—"
                  />
                )}
                actions={(
                  <SpaceBetween direction="horizontal" size="xs">
                    <span className="lot-danger-button">
                      <Button onClick={() => setShowQuarantine(true)}>
                        Quarantine all downstream
                      </Button>
                    </span>
                    <Button onClick={handleExportCsv} loading={isExporting}>
                      Export CSV
                    </Button>
                  </SpaceBetween>
                )}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{trace.root.code || trace.root.id}</span>
                  <LotKindBadge kind={trace.root.kind} />
                  <LotStatusBadge status={trace.root.status} />
                </div>
              </Header>
            )}
          >
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Downstream lots</Box>
                <Box fontSize="heading-xl">{downstreamLotCount}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Travelers</Box>
                <Box fontSize="heading-xl">{travelers.length}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Shipped</Box>
                <Box fontSize="heading-xl">{`${shippedCount} travelers`}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Order lines</Box>
                <Box fontSize="heading-xl">{orderLineCount}</Box>
              </div>
            </ColumnLayout>
          </Container>

          <Container header={<Header variant="h2">Downstream graph</Header>}>
            <SpaceBetween size="s">
              {downstreamLotCount === 0 && travelers.length === 0 ? (
                <Box color="text-status-inactive">Not consumed anywhere yet</Box>
              ) : (
                <TraceLotNode trace={trace} lotId={trace.root.id} />
              )}
            </SpaceBetween>
          </Container>

          {showQuarantine && (
            <QuarantineLotModal
              lot={trace.root}
              onClose={() => setShowQuarantine(false)}
            />
          )}
        </>
      )}
    </SpaceBetween>
  );
}
